import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const SEOvsAds = () => {
  const comparisonData = [
    { feature: "Time to Results", optionA: "3-6 months", optionB: "Immediate" },
    { feature: "Ongoing Cost", optionA: "Lower long-term", optionB: "$500-$3,000+/month" },
    { feature: "When You Stop", optionA: "Traffic continues", optionB: "Traffic stops" },
    { feature: "Click Cost", optionA: "$0 per click", optionB: "$5-$50+ per click" },
    { feature: "Trust Factor", optionA: "Higher (organic)", optionB: "Lower (ads marked)" },
    { feature: "Competition Level", optionA: "Content-based", optionB: "Budget-based" },
    { feature: "Scalability", optionA: "Compound growth", optionB: "Linear (pay more = more)" },
    { feature: "Best For", optionA: "Long-term growth", optionB: "Immediate leads" },
  ];

  const verdicts = [
    { label: "New Business", recommendation: "Ads first, then SEO" },
    { label: "Established Business", recommendation: "SEO with Ads support" },
    { label: "Limited Budget", recommendation: "SEO focus" },
    { label: "Aggressive Growth", recommendation: "Both together" },
    { label: "Best ROI", recommendation: "Combined strategy" },
  ];

  const faqs = [
    {
      question: "Should I invest in SEO or Google Ads for my lawn care business?",
      answer: "Ideally both, but if choosing one: new businesses benefit from Ads for immediate leads while building SEO; established businesses should prioritize SEO for sustainable growth with Ads for peak seasons."
    },
    {
      question: "How long does lawn care SEO take to work?",
      answer: "Typically 3-6 months to see significant rankings and traffic. However, competitive markets may take 6-12 months. The payoff is sustainable, low-cost leads once you rank."
    },
    {
      question: "How much do Google Ads cost for lawn care?",
      answer: "Expect $5-$30 per click for lawn care keywords. A lead typically costs $30-$100+ depending on competition. Monthly budgets usually start at $500-$1,500 for meaningful results."
    },
    {
      question: "Which generates better quality leads—SEO or Ads?",
      answer: "SEO leads often convert better because organic results have higher trust. However, Ads leads are more immediate and predictable. Quality depends on your targeting and landing pages."
    },
    {
      question: "Can I do SEO myself for my lawn care website?",
      answer: "Basic SEO, yes. But competitive lawn care markets require expertise in technical SEO, content strategy, and link building that most business owners don't have time to master."
    },
    {
      question: "What happens when I stop running Google Ads?",
      answer: "Leads stop immediately. That's the disadvantage of Ads—you're renting visibility. SEO builds owned assets that continue generating leads even without ongoing investment."
    },
    {
      question: "How much should I budget for lawn care SEO?",
      answer: "Professional SEO services range from $500-$2,000+/month. A quality website subscription with SEO included offers better value for most lawn care businesses starting out."
    },
    {
      question: "Is local SEO different from regular SEO?",
      answer: "Yes. Local SEO focuses on map pack rankings, Google Business Profile optimization, local keywords, and service area pages. It's essential for lawn care businesses serving specific geographic areas."
    },
    {
      question: "Can I use both SEO and Google Ads together?",
      answer: "Absolutely—this is the ideal strategy. Ads provide immediate visibility while SEO builds long-term organic traffic. Together, they dominate search results and maximize lead flow."
    },
    {
      question: "What's the best starting strategy for lawn care marketing?",
      answer: "Start with a professional website optimized for SEO, claim and optimize your Google Business Profile, then add Google Ads for immediate lead flow while SEO builds momentum."
    },
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/comparisons/seo-vs-google-ads/";

  return (
    <>
      <SEO 
        title="SEO vs Google Ads for Lawn Care Leads"
        description="SEO vs Google Ads for lawn care: Compare costs, timelines, and ROI. Learn when to use organic search vs paid ads for your landscaping business."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema 
        title="SEO vs Google Ads for Lawn Care Leads"
        description="SEO vs Google Ads for lawn care: Compare costs, timelines, and ROI for your landscaping business."
        url={CANONICAL}
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              SEO vs Google Ads for Lawn Care Leads: Which Should You Choose?
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Organic search or paid advertising? Here's how to choose the right strategy—or why you might need both.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">SEO vs Ads: Head-to-Head</h2>
              <p className="text-muted-foreground mb-6">
                Both <NavLink to="/lawn-care-website-seo/" className="text-primary hover:underline">SEO</NavLink> and 
                <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline"> Google Ads</NavLink> can 
                generate lawn care leads. Here's how they compare:
              </p>
              <ComparisonTable 
                optionAName="SEO"
                optionBName="Google Ads"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">When SEO Makes Sense</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>You can wait 3-6 months for results to compound</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>You want sustainable, long-term lead generation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Your budget favors upfront investment over ongoing ad spend</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>You're building <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area pages</NavLink> for multiple cities</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">When Google Ads Makes Sense</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>You need leads immediately (new business or peak season)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>You have budget to sustain $500-$2,000+/month ad spend</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Competitors dominate organic results and you can't wait</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>You want predictable, scalable lead flow</span>
                </li>
              </ul>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Best Strategy: Both Together</h2>
              <p className="text-muted-foreground mb-4">
                Top-performing lawn care companies use SEO and Ads together. Here's the playbook:
              </p>
              <ol className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                  <span>Start with a <NavLink to="/websites/" className="text-primary hover:underline">professional SEO-optimized website</NavLink></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                  <span>Optimize your <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">Google Business Profile</NavLink> for local visibility</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                  <span>Launch targeted <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline">Google Ads campaigns</NavLink> for immediate leads</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                  <span>Build <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">service pages</NavLink> and content for organic growth</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</span>
                  <span>Reduce ad spend as organic rankings improve</span>
                </li>
              </ol>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/website-only-vs-website-plus-gbp/", text: "Website vs Website + GBP" },
                { href: "/lawn-care-website-seo/", text: "Lawn Care Website SEO Guide" },
                { href: "/growth-add-ons/google-ads/", text: "Google Ads Services" },
                { href: "/growth-add-ons/gbp/", text: "GBP Optimization" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default SEOvsAds;
